import { useState, useEffect } from 'react'
import { Car, Menu, LogIn } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
} from '@/components/ui/sheet'
import { Link } from 'react-router-dom'
import { useI18n } from '@/store/I18nContext'
import { LanguageSwitcher } from './LanguageSwitcher'

interface NavbarProps {
  onOpenDemo: () => void
}

export function Navbar({ onOpenDemo }: NavbarProps) {
  const { t } = useI18n()
  const [active, setActive] = useState('')
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)

  const navLinks = [
    { id: 'recursos', label: t('nav.dashboard') },
    { id: 'operacao', label: t('nav.operation') },
    { id: 'mensalistas', label: t('nav.subscribers') },
    { id: 'precos', label: t('nav.plans') },
  ]

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
      let current = ''
      for (const link of navLinks) {
        const el = document.getElementById(link.id)
        if (el && window.scrollY >= el.offsetTop - 150) {
          current = link.id
        }
      }
      setActive(current)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [navLinks])

  const scrollTo = (id: string) => {
    setMobileOpen(false)
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  return (
    <nav
      className={cn(
        'fixed top-0 w-full z-50 transition-all duration-300 border-b',
        scrolled
          ? 'bg-background/95 backdrop-blur-md shadow-sm border-border'
          : 'bg-transparent border-transparent',
      )}
    >
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        <div
          className="flex items-center gap-2 cursor-pointer group"
          onClick={() => window.scrollTo(0, 0)}
        >
          <div className="bg-primary text-primary-foreground p-1.5 rounded-lg group-hover:scale-105 transition-transform">
            <Car className="h-6 w-6" />
          </div>
          <span className="font-extrabold text-xl tracking-tighter">
            Parkko
          </span>
        </div>

        <div className="hidden md:flex items-center gap-6">
          {navLinks.map((link) => (
            <button
              key={link.id}
              onClick={() => scrollTo(link.id)}
              className={cn(
                'text-sm font-semibold tracking-wide transition-colors hover:text-primary',
                active === link.id ? 'text-primary' : 'text-muted-foreground',
              )}
            >
              {link.label}
            </button>
          ))}
          <div className="flex items-center gap-3 ml-4 border-l pl-4">
            <LanguageSwitcher />
            <Link to="/dashboard">
              <Button
                variant="ghost"
                className="font-bold tracking-tight gap-2"
              >
                <LogIn className="h-4 w-4" /> {t('nav.login')}
              </Button>
            </Link>
            <Button onClick={onOpenDemo} className="font-bold tracking-tight">
              {t('nav.getStarted')}
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <LanguageSwitcher />
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon">
                <Menu className="h-6 w-6" />
              </Button>
            </SheetTrigger>
            <SheetContent
              side="right"
              className="flex flex-col gap-6 pt-12 font-sans"
            >
              <SheetTitle className="sr-only">Menu de Navegação</SheetTitle>
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollTo(link.id)}
                  className="text-lg font-bold tracking-tight text-left"
                >
                  {link.label}
                </button>
              ))}
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t">
                <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                  <Button
                    variant="outline"
                    className="w-full font-bold tracking-tight h-12 gap-2"
                  >
                    <LogIn className="h-4 w-4" /> {t('nav.login')}
                  </Button>
                </Link>
                <Button
                  onClick={() => {
                    setMobileOpen(false)
                    onOpenDemo()
                  }}
                  className="w-full font-bold tracking-tight h-12"
                >
                  {t('nav.getStarted')}
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  )
}
